import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveTable, ResponsiveTableColumn } from '@/components/common/ResponsiveTable';
import { Alert } from '@/components/common/Alert';
import { faqService, FAQ } from '@/services/cms.service';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import '@/styles/dashboard.css';

const ITEMS_PER_PAGE = 10;

export const FAQPage = () => {
  const { t } = useTranslation();
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState<FAQ | null>(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: '',
    status: 'published' as 'published' | 'draft',
    display_order: 0
  });

  useEffect(() => {
    loadFAQs();
  }, []);

  const loadFAQs = async () => {
    try {
      setLoading(true);
      const data = await faqService.getAll();
      setFaqs(data);
      setError(null);
    } catch (err: any) {
      console.error('Erreur lors du chargement des FAQ:', err);
      setError(t('adminFAQ.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredFAQs = useMemo(() => {
    let filtered = [...faqs];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(faq =>
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query) ||
        faq.category?.toLowerCase().includes(query)
      );
    }

    if (selectedStatus) {
      filtered = filtered.filter(faq => faq.status === selectedStatus);
    }

    // Trier par display_order (les données viennent déjà triées du backend, mais on s'assure)
    filtered.sort((a, b) => {
      const orderA = a.display_order ?? 0;
      const orderB = b.display_order ?? 0;
      if (orderA !== orderB) {
        return orderA - orderB; // Tri croissant (0, 1, 2, ...)
      }
      // Si même ordre, trier par ID décroissant (plus récent en premier)
      return (
    <>
b.id ?? 0
    </>
  ) - (a.id ?? 0);
    });

    return filtered;
  }, [faqs, searchQuery, selectedStatus]);

  const totalPages = Math.ceil(filteredFAQs.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedFAQs = filteredFAQs.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedStatus]);

  const handleAdd = () => {
    setEditingFAQ(null);
    setFormData({
      question: '',
      answer: '',
      category: '',
      status: 'published',
      display_order: 0
    });
    setShowModal(true);
  };

  const handleEdit = (faq: FAQ) => {
    setEditingFAQ(faq);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      category: faq.category || '',
      status: faq.status,
      display_order: faq.display_order ?? 0
    });
    setShowModal(true);
  };

  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; message: string } | null>(null);

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('adminFAQ.deleteConfirm'))) {
      return;
    }

    try {
      await faqService.delete(id);
      await loadFAQs();
      setNotification({ type: 'success', message: t('adminFAQ.deleteSuccess') });
    } catch (err: any) {
      setNotification({ type: 'error', message: t('adminFAQ.deleteError') + (err.message || '') });
    }
  };

  const handleToggleStatus = async (faq: FAQ) => {
    try {
      const newStatus = faq.status === 'published' ? 'draft' : 'published';
      await faqService.toggleStatus(faq.id, newStatus);
      await loadFAQs();
      setNotification({ type: 'success', message: t('adminFAQ.updateSuccess') });
    } catch (err: any) {
      setNotification({ type: 'error', message: t('adminFAQ.toggleError') + (err.message || '') });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.question.trim() || !formData.answer.trim()) {
      setNotification({ type: 'warning', message: t('adminFAQ.questionAnswerRequired') });
      return;
    }

    try {
      // S'assurer que display_order est un nombre valide
      const dataToSave = {
        ...formData,
        display_order: typeof formData.display_order === 'number' 
          ? formData.display_order 
          : parseInt(String(formData.display_order || 0), 10) || 0
      };
      
      console.log('💾 Sauvegarde FAQ avec données:', dataToSave);
      
      if (editingFAQ) {
        await faqService.update(editingFAQ.id, dataToSave);
        console.log('✅ FAQ mise à jour avec succès');
      } else {
        const result = await faqService.create(dataToSave);
        console.log('✅ FAQ créée avec succès:', result);
      }
      setShowModal(false);
      await loadFAQs();
      setNotification({ type: 'success', message: editingFAQ ? t('adminFAQ.saveSuccess') : t('adminFAQ.createSuccess') });
    } catch (err: any) {
      console.error('❌ Erreur complète:', err);
      console.error('Erreur response:', err.response);
      const errorMessage = err.response?.data?.error || err.message || 'Erreur inconnue';
      const statusCode = err.response?.status || 'N/A';
      setNotification({ type: 'error', message: `Erreur lors de la sauvegarde (${statusCode}): ${errorMessage}` });
    }
  };

  const columns: ResponsiveTableColumn<FAQ>[] = [
    {
      key: 'question',
      label: t('adminFAQ.question'),
      render: (faq) => (
        <div>
          <div className="table-cell-name mb-1">{faq.question}</div>
          {faq.category && (
            <span className="status-badge status-badge-neutral text-xs">
              {faq.category}
            </span>
          )}
        </div>
      )
    },
    {
      key: 'answer',
      label: t('adminFAQ.answer'),
      render: (faq) => (
        <div className="table-cell-text text-truncate" style={{ maxWidth: '400px' }}>
          {faq.answer}
        </div>
      ),
      mobileHidden: true
    },
    {
      key: 'status',
      label: t('adminFAQ.status'),
      render: (faq) => (
        <button
          onClick={() => handleToggleStatus(faq)}
          className={`status-badge ${faq.status === 'published' ? 'status-badge-success' : 'status-badge-neutral'}`}
        >
          {faq.status === 'published' ? t('adminFAQ.publishedSingle') : t('adminFAQ.draftSingle')}
        </button>
      )
    },
    {
      key: 'display_order',
      label: t('adminFAQ.order'),
      render: (faq) => (
        <span className={`font-semibold text-sm ${faq.display_order === 0 ? 'text-body' : 'text-cranberry'}`}>
          {faq.display_order ?? 0}
        </span>
      ),
      mobileHidden: true
    },
    {
      key: 'actions',
      label: t('adminFAQ.actions'),
      render: (faq) => (
        <div className="product-actions">
          <button
            onClick={() => handleEdit(faq)}
            className="action-btn action-edit"
            title={t('adminFAQ.edit')}
            aria-label={t('adminFAQ.edit')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
              <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
            </svg>
          </button>
          <button
            onClick={() => handleDelete(faq.id)}
            className="action-btn action-delete"
            title={t('adminFAQ.delete')}
            aria-label={t('adminFAQ.delete')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
              <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
            </svg>
          </button>
        </div>
      )
    }
  ];

  return (
    <>
    <div className="table-card">
              <div className="table-header">
                <h3 className="table-title">{t('adminFAQ.pageTitle', { count: filteredFAQs.length })}</h3>
                <div className="flex gap-2 items-center">
                  {(searchQuery || selectedStatus) && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedStatus('');
                      }}
                      className="btn btn-secondary btn-sm"
                    >
                      {t('adminFAQ.resetFilters')}
                    </button>
                  )}
                  <button
                    onClick={handleAdd}
                    className="btn btn-primary btn-sm"
                  >
                    + {t('adminFAQ.add')}
                  </button>
                </div>
              </div>

              {/* Filtres */}
              <div className="filters-bar">
                <div className="form-group-flex">
                  <label className="form-label">{t('adminFAQ.search')}</label>
                  <input
                    type="text"
                    placeholder={t('adminFAQ.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div style={{ minWidth: '150px' }}>
                  <label className="form-label">{t('adminFAQ.status')}</label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="form-select"
                  >
                    <option value="">{t('adminFAQ.allStatuses')}</option>
                    <option value="published">{t('adminFAQ.publishedPlural')}</option>
                    <option value="draft">{t('adminFAQ.draftPlural')}</option>
                  </select>
                </div>
              </div>

              {notification && (
                <Alert
                  type={notification.type}
                  message={notification.message}
                  onClose={() => setNotification(null)}
                />
              )}

              {error ? (
                <Alert type="error" message={error} />
              ) : (
                <ResponsiveTable
                  columns={columns}
                  data={paginatedFAQs}
                  loading={loading}
                  loadingMessage={t('adminFAQ.loading')}
                  emptyMessage={
                    searchQuery || selectedStatus
                      ? t('adminFAQ.noSearch')
                      : t('adminFAQ.empty')
                  }
                  mobileCardTitle={(faq) => (
                    <div>
                      <div className="table-cell-name mb-1">{faq.question}</div>
                      <div className="table-cell-description">{faq.answer.substring(0, 100)}...</div>
                    </div>
                  )}
                  mobileCardActions={(faq) => (
                    <div className="product-actions" style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleEdit(faq)}
                        className="action-btn action-edit"
                        title={t('adminFAQ.edit')}
                        aria-label={t('adminFAQ.edit')}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(faq.id)}
                        className="action-btn action-delete"
                        title={t('adminFAQ.delete')}
                        aria-label={t('adminFAQ.delete')}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                        </svg>
                      </button>
                    </div>
                  )}
                  tableClassName="products-table"
                />
              )}

              {/* Pagination */}
              {!error && totalPages > 1 && (
                <div style={{
                  padding: '20px',
                  borderTop: '1px solid #e0e0e0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    {t('adminFAQ.displaying', { start: startIndex + 1, end: Math.min(endIndex, filteredFAQs.length), total: filteredFAQs.length })}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        background: currentPage === 1 ? '#f5f5f5' : 'white',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        opacity: currentPage === 1 ? 0.5 : 1
                      }}
                    >
                      {t('adminFAQ.previous')}
                    </button>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
    <>

                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              style={{
                                padding: '8px 12px',
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                background: currentPage === page ? '#8B204E' : 'white',
                                color: currentPage === page ? 'white' : '#333',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: currentPage === page ? 600 : 400
                              }}
                            >
                              {page}
                            </button>
                          
    </>
  );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return <span key={page} style={{ padding: '8px' }}>...</span>;
                        }
                        return null;
                      })}
                    </div>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        background: currentPage === totalPages ? '#f5f5f5' : 'white',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        opacity: currentPage === totalPages ? 0.5 : 1
                      }}
                    >
                      {t('adminFAQ.next')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Ajout/Édition */}
            {showModal && (
              <div 
                className="modal-overlay" 
                onClick={() => setShowModal(false)}
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-title"
              >
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2 id="modal-title" className="modal-title">
                      {editingFAQ ? t('adminFAQ.modalEdit') : t('adminFAQ.modalAdd')}
                    </h2>
                    <button
                      onClick={() => setShowModal(false)}
                      className="modal-close"
                      aria-label={t('adminFAQ.close')}
                    >
                      <XMarkIcon className="btn-icon" />
                    </button>
                  </div>

                  <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                      <div className="form-group">
                        <label className="form-label form-label-required">{t('adminFAQ.questionLabel')}</label>
                        <input
                          type="text"
                          className="form-input"
                          value={formData.question}
                          onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label form-label-required">{t('adminFAQ.answerLabel')}</label>
                        <textarea
                          className="form-textarea"
                          value={formData.answer}
                          onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                          rows={6}
                          required
                        />
                      </div>

                    <div className="form-group">
                      <label className="form-label">{t('adminFAQ.category')}</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        placeholder={t('adminFAQ.categoryPlaceholder')}
                      />
                    </div>

                    <div className="flex gap-3">
                      <div className="form-group flex-1">
                        <label className="form-label">{t('adminFAQ.status')}</label>
                        <select
                          className="form-select"
                          value={formData.status}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value as 'published' | 'draft' })}
                        >
                          <option value="published">{t('adminFAQ.publishedSingle')}</option>
                          <option value="draft">{t('adminFAQ.draftSingle')}</option>
                        </select>
                      </div>

                      <div className="form-group flex-1">
                        <label className="form-label">{t('adminFAQ.displayOrder')}</label>
                        <input
                          type="number"
                          className="form-input"
                          value={formData.display_order ?? 0}
                          onChange={(e) => {
                            const value = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                            setFormData({ ...formData, display_order: isNaN(value) ? 0 : value });
                          }}
                          min="0"
                          step="1"
                          placeholder={t('adminFAQ.zero')}
                        />
                        <div className="form-help-text">
                          {t('adminFAQ.formOrderHelp')}
                        </div>
                      </div>
                    </div>
                    </div>

                    <div className="modal-footer">
                      <button
                        type="button"
                        onClick={() => setShowModal(false)}
                        className="btn btn-secondary"
                      >
                        {t('adminFAQ.cancel')}
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                      >
                        {editingFAQ ? t('adminFAQ.save') : t('adminFAQ.create')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
    </>
  );
};
